/**
 * The surface of a water barrier. Terrain.ts draws one rectangular water plane (the pond);
 * a river needs a band along a polyline, so it gets its own mesh here, built the same way:
 * a toon material patched for the MRT target, albedo computed per pixel from world position,
 * every tone taken from the palette, every pattern anchored to the world so nothing swims.
 *
 * The tells (05 §5.1) are the point. Ice: WEAK ice is a dark blue patch crazed with wide
 * black cracks; a THICK-ice crossing is snow-covered and unbroken; ordinary ice is pale with
 * the odd hairline. Water: a FORD is pale, busy riffles; the channel darkens with depth.
 * A crossing with a bridge on it has neither: there the deck is the tell (river.ts `openCrossings`).
 */

import { Mesh, MeshToonMaterial, type Texture, Vector3 } from 'three';
import type { PropDef, WaterBarrierDef } from '../../sim/types';
import { MeshBuilder } from '../kit/MeshBuilder';
import { NOISE_GLSL, OUTLINE, patchMrt, sharedUniforms } from '../materials/toon';
import type { Palette } from '../palette';
import {
  BANK_IN, BANK_OUT, CROSSING_FADE, CROSSING_HALF_WIDTH, FORD_BED, openCrossings, RIVER_BED, RIVER_LEVEL,
} from './river';

/** Fixed uniform array sizes; a course or hazard list longer than this is truncated. */
const MAX_POINTS = 32;
const MAX_WEAK = 24;
const MAX_CROSSINGS = 8;

const padded = (values: number[], length: number): number[] => {
  const out = values.slice(0, length);
  while (out.length < length) out.push(0);
  return out;
};

/** Where a crossing lane's look fades in and out, as GLSL literals; its true edge is half-way. */
const LANE_INNER = (CROSSING_HALF_WIDTH - CROSSING_FADE).toFixed(2);
const LANE_OUTER = (CROSSING_HALF_WIDTH + CROSSING_FADE).toFixed(2);

const RIVER_GLSL = /* glsl */ `
varying vec3 vWorldPos;
uniform vec2 uCourse[${MAX_POINTS}];
uniform int uCourseCount;
uniform vec3 uWeak[${MAX_WEAK}];
uniform int uWeakCount;
uniform float uCross[${MAX_CROSSINGS}];
uniform int uCrossCount;
uniform vec4 uShape; // half width, bed depth, ford bed depth, surface level
uniform vec3 uTones[4];
uniform vec3 uSnow;
uniform float uIce;
uniform vec2 uCamTexel;
uniform float uTime;
${NOISE_GLSL}
float courseDistance(vec2 p) {
  float best = 1e9;
  for (int i = 1; i < ${MAX_POINTS}; i++) {
    if (i >= uCourseCount) break;
    vec2 a = uCourse[i - 1];
    vec2 v = uCourse[i] - a;
    float t = clamp(dot(p - a, v) / max(dot(v, v), 1e-6), 0.0, 1.0);
    best = min(best, distance(p, a + v * t));
  }
  return best;
}
float crossing(float x) {
  float w = 0.0;
  for (int i = 0; i < ${MAX_CROSSINGS}; i++) {
    if (i >= uCrossCount) break;
    w = max(w, 1.0 - smoothstep(${LANE_INNER}, ${LANE_OUTER}, abs(x - uCross[i])));
  }
  return w;
}
vec3 riverAlbedo() {
  vec2 p = vWorldPos.xz;
  vec2 cell = floor(gl_FragCoord.xy) + uCamTexel;
  float safe = crossing(p.x);
  // The same bank profile terrainFromDef carves, so the tones follow the real bed.
  float bed = mix(uShape.y, uShape.z, uIce > 0.5 ? 0.0 : safe);
  float bank = 1.0 - smoothstep(uShape.x - ${BANK_IN.toFixed(2)}, uShape.x + ${BANK_OUT.toFixed(2)}, courseDistance(p));
  float depth = bed * bank + uShape.w;

  if (uIce > 0.5) {
    if (depth < 0.16) return uSnow; // drifted in along the banks
    float weak = 0.0;
    for (int i = 0; i < ${MAX_WEAK}; i++) {
      if (i >= uWeakCount) break;
      // A ragged edge: a perfect circle of bad ice would look like a marker, not like ice. The
      // rag only ever grows the patch - ice drawn as sound must never be inside the sim's circle.
      float r = uWeak[i].z * (1.0 + 0.3 * valueNoise(p * 0.7 + 4.2));
      weak = max(weak, step(distance(p, uWeak[i].xy), r));
    }
    if (weak > 0.5 && safe < 0.5) {
      float c1 = abs(valueNoise(p * 1.5 + 3.3) - 0.5);
      float c2 = abs(valueNoise(p * 2.9 + 9.1) - 0.5);
      if (c1 < 0.04 || c2 < 0.035) return uTones[0];
      return valueNoise(p * 0.9 + 5.0) > 0.62 ? uTones[2] : uTones[1];
    }
    float dust = valueNoise(p * 0.4 + 11.0) + (hash12(cell) - 0.5) * 0.25;
    if (safe > 0.5) return dust > 0.3 ? uSnow : uTones[3];
    float crackA = abs(valueNoise(p * 0.55) - 0.5);
    float crackB = abs(valueNoise(p * 1.3 + 7.7) - 0.5);
    if (crackA < 0.012 || crackB < 0.008) return uTones[1];
    if (dust > 0.74) return uSnow;
    return dust > 0.5 ? uTones[3] : uTones[2];
  }

  // Open water, stepped at 5 Hz like hand-drawn frames; the ripples drift east with the current.
  float tick = floor(uTime * 5.0);
  float foamEdge = 0.08 + 0.05 * valueNoise(p * 1.1 + vec2(tick * 0.37, tick * 0.19));
  if (depth < foamEdge) return uTones[3];
  if (hash12(cell + tick * 13.0) > 0.9965) return uTones[3];
  float ripple = valueNoise(vec2(p.x * 0.35 - tick * 0.23, p.y * 1.6));
  if (safe > 0.5) return valueNoise(vec2(p.x * 1.2 - tick * 0.4, p.y * 2.4)) > 0.5 ? uTones[3] : uTones[2];
  if (depth < 0.3) return ripple > 0.62 ? uTones[3] : uTones[2];
  if (depth < 0.5) return ripple > 0.66 ? uTones[2] : uTones[1];
  return ripple > 0.70 ? uTones[1] : uTones[0];
}
`;

/** Band of quads along the course, a metre wider than the channel; the banks hide the excess. */
function ribbon(barrier: WaterBarrierDef, palette: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  const reach = barrier.course.width / 2 + BANK_OUT + 0.4;
  const pts = barrier.course.points;
  const color = palette.color('water.2');
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (!a || !b) continue;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (len < 1e-6) continue;
    const ux = (b[0] - a[0]) / len;
    const uz = (b[1] - a[1]) / len;
    // Square caps run `reach` past each end, so consecutive segments overlap and bends have no gaps.
    // Overlaps are harmless: the albedo depends on world position alone.
    const corner = (end: readonly [number, number], along: number, side: number): Vector3 =>
      new Vector3(end[0] + ux * along * reach - uz * side * reach, 0, end[1] + uz * along * reach + ux * side * reach);
    const q = [corner(a, -1, 1), corner(b, 1, 1), corner(b, 1, -1), corner(a, -1, -1)] as const;
    // MeshBuilder drops nothing for winding, but the material is single-sided: make the face look up.
    const up = (q[1].x - q[0].x) * (q[2].z - q[0].z) - (q[1].z - q[0].z) * (q[2].x - q[0].x) < 0;
    if (up) mb.quad(q[0], q[1], q[2], q[3], color);
    else mb.quad(q[3], q[2], q[1], q[0], color);
  }
  return mb;
}

/**
 * Surface mesh for one barrier, positioned at `RIVER_LEVEL`. Dispose its geometry and material with it.
 * `props`: the map's props - a crossing a `bridge` stands on gets no ford riffle or sound-ice tell (the deck is the
 * tell, and covers the whole lane); leave it out and every crossing is drawn.
 */
export function buildRiverSurface(
  barrier: WaterBarrierDef,
  palette: Palette,
  gradientMap: Texture,
  props: readonly PropDef[] = [],
): Mesh {
  const material = new MeshToonMaterial({ gradientMap });
  const tones = [0, 1, 2, 3].flatMap((s) => {
    const c = palette.color(`water.${s}`);
    return [c.r, c.g, c.b];
  });
  const snow = palette.color('ground.3');
  const pts = barrier.course.points.slice(0, MAX_POINTS);
  const crossings = openCrossings(barrier, props);

  material.onBeforeCompile = (shader) => {
    patchMrt(shader, OUTLINE.none);
    const u = shader.uniforms;
    u['uCourse'] = { value: padded(pts.flatMap(([x, z]) => [x, z]), MAX_POINTS * 2) };
    u['uCourseCount'] = { value: pts.length };
    u['uWeak'] = { value: padded(barrier.weakIce.slice(0, MAX_WEAK).flatMap((w) => [w.x, w.z, w.r]), MAX_WEAK * 3) };
    u['uWeakCount'] = { value: Math.min(MAX_WEAK, barrier.weakIce.length) };
    u['uCross'] = { value: padded(crossings, MAX_CROSSINGS) };
    u['uCrossCount'] = { value: Math.min(MAX_CROSSINGS, crossings.length) };
    u['uShape'] = { value: [barrier.course.width / 2, RIVER_BED, FORD_BED, RIVER_LEVEL] };
    u['uTones'] = { value: tones };
    u['uSnow'] = { value: [snow.r, snow.g, snow.b] };
    u['uIce'] = { value: barrier.surface === 'ice' ? 1 : 0 };
    u['uCamTexel'] = sharedUniforms.uCamTexel;
    u['uTime'] = sharedUniforms.uTime;
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'varying vec3 vWorldPos;\nvoid main() {')
      .replace(
        '#include <project_vertex>',
        '#include <project_vertex>\n  vWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;',
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', `${RIVER_GLSL}\nvoid main() {`)
      .replace('#include <color_fragment>', 'diffuseColor.rgb = riverAlbedo();');
  };
  material.customProgramCacheKey = () => 'toon-mrt-river';

  const mesh = new Mesh(ribbon(barrier, palette).build(), material);
  mesh.position.y = RIVER_LEVEL;
  mesh.receiveShadow = true;
  mesh.name = 'river';
  return mesh;
}
